/**
 * MCK Gallery Optimizer v2.0
 * Provides instant client-side image filtering when changing color variants.
 * Includes skeleton loading animation and performance optimizations.
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    // Color option labels (lowercase) - matches your translation file
    colorLabels: ['colore', 'color', 'colour', 'farbe'],
    // Selector for product gallery
    gallerySelector: 'product-gallery',
    // Selector for variant picker
    variantPickerSelector: 'variant-picker',
    // Debounce delay (ms)
    debounceDelay: 5,
    // Enable debug logging (set to true to see console output)
    debug: false,
    // Skeleton loading duration (ms) - minimum time to show skeleton
    skeletonMinDuration: 150
  };

  /**
   * Debug logger
   */
  function log(...args) {
    if (CONFIG.debug) {
      console.log('[MCK Gallery]', ...args);
    }
  }

  /**
   * Performance timer
   */
  function startTimer(label) {
    if (CONFIG.debug) {
      console.time(`[MCK Gallery] ${label}`);
    }
    return label;
  }

  function endTimer(label) {
    if (CONFIG.debug) {
      console.timeEnd(`[MCK Gallery] ${label}`);
    }
  }

  /**
   * Normalize color value for comparison
   * Converts "Military Green", "military-green", or "White/Black" to normalized format
   * All separators (hyphens, slashes, spaces) become spaces for consistent matching
   */
  function normalizeColor(value) {
    if (!value) return '';
    return value
      .toLowerCase()
      .replace(/-/g, ' ')   // Convert hyphens to spaces
      .replace(/\//g, ' ')  // Convert slashes to spaces
      .replace(/\s+/g, ' ') // Collapse multiple spaces
      .trim();
  }

  /**
   * Extract color from alt text
   * e.g., "#colore_ocean-blue" -> "ocean blue"
   */
  function extractColorFromAlt(alt) {
    if (!alt || !alt.includes('#')) return null;
    
    const parts = alt.split('#');
    const groupTag = parts[parts.length - 1]; // Get last part after #
    
    if (!groupTag.includes('_')) return null;
    
    const underscoreIndex = groupTag.indexOf('_');
    const optionName = groupTag.substring(0, underscoreIndex).toLowerCase();
    const colorValue = groupTag.substring(underscoreIndex + 1).trim();
    
    // Check if this is a color option
    if (!CONFIG.colorLabels.includes(optionName)) return null;
    
    return normalizeColor(colorValue);
  }

  /**
   * Get the currently selected color from the variant picker
   */
  function getSelectedColor(variantPicker) {
    // Try to find the color option
    const options = variantPicker.querySelectorAll('.variant-picker__option');
    
    for (const optionGroup of options) {
      const legend = optionGroup.querySelector('legend');
      if (!legend) continue;
      
      const optionName = legend.textContent.toLowerCase();
      const isColorOption = CONFIG.colorLabels.some(label => optionName.includes(label));
      
      if (isColorOption) {
        // Find the checked input
        const checkedInput = optionGroup.querySelector('input[type="radio"]:checked');
        if (checkedInput) {
          // Get the label text for this input
          const label = optionGroup.querySelector(`label[for="${checkedInput.id}"]`);
          if (label) {
            // Get text content, excluding child elements like tooltips
            const labelText = label.textContent || label.getAttribute('aria-label') || '';
            const color = normalizeColor(labelText.trim());
            log('Selected color:', color);
            return color;
          }
        }
      }
    }
    
    return null;
  }

  /**
   * Add skeleton loading state to items
   */
  function addSkeletonState(items) {
    items.forEach(item => {
      item.classList.add('mck-loading');
      item.classList.remove('mck-loaded');
    });
  }

  /**
   * Remove skeleton loading state from items
   */
  function removeSkeletonState(items) {
    items.forEach(item => {
      item.classList.remove('mck-loading');
      item.classList.add('mck-loaded');
    });
  }

  /**
   * Instantly filter gallery images based on selected color
   * Optimized for speed with batch DOM updates
   */
  function filterGalleryImages(gallery, selectedColor, showSkeleton = true) {
    if (!selectedColor) {
      log('No color selected, skipping filter');
      return;
    }
    
    const timer = startTimer('filterGalleryImages');
    
    const mediaItems = gallery.querySelectorAll('.product-gallery__media');
    const thumbnails = gallery.querySelectorAll('.product-gallery__thumbnail');
    const pageDots = gallery.querySelectorAll('page-dots button');
    
    log(`Filtering ${mediaItems.length} media items for color: "${selectedColor}"`);
    
    // Pre-calculate all visibility states (fast loop, no DOM changes)
    const visibilityMap = new Map();
    let firstVisibleIndex = -1;
    let visibleCount = 0;
    
    mediaItems.forEach((item, index) => {
      const img = item.querySelector('img');
      const alt = img?.alt || '';
      const mediaColor = extractColorFromAlt(alt);
      
      // MCK FIX: Only show images WITH a matching #colore tag
      // Images WITHOUT #colore tags are hidden (except first/default as fallback)
      // - Has color tag AND matches selected color → SHOW
      // - Has color tag AND doesn't match → HIDE
      // - NO color tag → HIDE (except index 0 as fallback)
      let shouldShow = false;
      
      if (mediaColor) {
        // Has a color tag - show only if it matches
        shouldShow = mediaColor === selectedColor;
      } else {
        // No color tag - hide it (keep index 0 as fallback only)
        shouldShow = false;
      }
      
      visibilityMap.set(index, shouldShow);
      
      if (shouldShow) {
        visibleCount++;
        if (firstVisibleIndex === -1) {
          firstVisibleIndex = index;
        }
      }
      
      if (CONFIG.debug) {
        const hasTag = mediaColor ? 'YES' : 'NO';
        log(`  [${index}] alt="${alt}" -> hasTag=${hasTag}, mediaColor="${mediaColor || 'none'}" ${shouldShow ? '✓ SHOW' : '✗ HIDE'}`);
      }
    });
    
    log(`Visible items: ${visibleCount}/${mediaItems.length}, first visible index: ${firstVisibleIndex}`);
    
    // Safety fallback: If NO images are visible, show the first one to avoid empty gallery
    if (visibleCount === 0 && mediaItems.length > 0) {
      log('WARNING: No matching images found! Showing first image as fallback.');
      visibilityMap.set(0, true);
      firstVisibleIndex = 0;
      visibleCount = 1;
    }
    
    // Mark gallery as having been filtered (enables animation CSS)
    gallery.setAttribute('data-mck-filtered', 'true');
    
    // Apply all DOM changes in a single requestAnimationFrame for best performance
    requestAnimationFrame(() => {
      // Batch all visibility changes
      visibilityMap.forEach((shouldShow, index) => {
        const mediaItem = mediaItems[index];
        const thumbnail = thumbnails[index];
        const pageDot = pageDots[index];
        
        // Use hidden attribute (fastest way to hide elements)
        mediaItem.hidden = !shouldShow;
        if (thumbnail) thumbnail.hidden = !shouldShow;
        if (pageDot) pageDot.hidden = !shouldShow;
      });
      
      // Navigate carousel to first visible item
      if (firstVisibleIndex >= 0) {
        const carousel = gallery.querySelector('media-carousel');
        if (carousel && typeof carousel.select === 'function') {
          // Calculate visible index (position among visible items only)
          let visibleIndex = 0;
          for (let i = 0; i < firstVisibleIndex; i++) {
            if (visibilityMap.get(i)) visibleIndex++;
          }
          
          // Use select without animation for instant response
          try {
            carousel.select(0, { animate: false }); // Always go to first visible
          } catch (e) {
            log('Carousel select error:', e);
          }
        }
      }
      
      endTimer(timer);
    });
  }

  /**
   * Debounce helper
   */
  function debounce(fn, delay) {
    let timeoutId;
    return function(...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  /**
   * Initialize optimizer for a product gallery
   */
  function initGalleryOptimizer(gallery) {
    const formId = gallery.getAttribute('form');
    if (!formId) {
      log('No form ID found on gallery');
      return;
    }
    
    const form = document.getElementById(formId) || document.forms[formId];
    const variantPicker = document.querySelector(`${CONFIG.variantPickerSelector}[form-id="${formId}"]`) ||
                          document.querySelector(CONFIG.variantPickerSelector);
    
    if (!variantPicker) {
      log('No variant picker found');
      return;
    }
    
    log('Initializing gallery optimizer', { formId, gallery, variantPicker });
    
    // Mark images as loaded initially (no skeleton on first load)
    const mediaItems = gallery.querySelectorAll('.product-gallery__media');
    mediaItems.forEach(item => {
      item.classList.add('mck-loaded');
    });
    
    // Immediate filter function (minimal debounce for responsiveness)
    const immediateFilter = () => {
      const selectedColor = getSelectedColor(variantPicker);
      if (selectedColor) {
        filterGalleryImages(gallery, selectedColor, false);
      }
    };
    
    // Debounced filter for rapid clicks
    const debouncedFilter = debounce(immediateFilter, CONFIG.debounceDelay);
    
    // Listen for input changes on the variant picker
    variantPicker.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"]')) {
        // Check if this is a color option change
        const optionGroup = event.target.closest('.variant-picker__option');
        if (optionGroup) {
          const legend = optionGroup.querySelector('legend');
          if (legend) {
            const optionName = legend.textContent.toLowerCase();
            const isColorOption = CONFIG.colorLabels.some(label => optionName.includes(label));
            
            if (isColorOption) {
              log('Color option changed, filtering...');
              // Run immediately for instant feedback
              immediateFilter();
            }
          }
        }
      }
    }, { capture: true });
    
    // Also listen for click events on color swatches (for custom swatch implementations)
    variantPicker.addEventListener('click', (event) => {
      const swatch = event.target.closest('.color-swatch, .block-swatch');
      if (swatch) {
        log('Swatch clicked, scheduling filter...');
        // Small delay to let the radio change happen first
        setTimeout(immediateFilter, 0);
      }
    }, { capture: true });
    
    // Mark as initialized
    gallery.dataset.mckOptimized = 'true';
    log('Gallery optimizer initialized successfully');
  }

  /**
   * Initialize all galleries on the page
   */
  function initAllGalleries() {
    const galleries = document.querySelectorAll(CONFIG.gallerySelector + ':not([data-mck-optimized])');
    log(`Found ${galleries.length} galleries to initialize`);
    galleries.forEach(initGalleryOptimizer);
  }

  /**
   * Enable debug mode
   */
  function enableDebug() {
    CONFIG.debug = true;
    console.log('[MCK Gallery] Debug mode ENABLED. Color changes will be logged.');
    console.log('[MCK Gallery] Current state:', {
      config: CONFIG,
      galleries: document.querySelectorAll(CONFIG.gallerySelector).length,
      optimizedGalleries: document.querySelectorAll('[data-mck-optimized]').length
    });
  }

  /**
   * Disable debug mode
   */
  function disableDebug() {
    CONFIG.debug = false;
    console.log('[MCK Gallery] Debug mode DISABLED.');
  }

  /**
   * Test the optimizer manually
   */
  function testFilter(colorName) {
    const gallery = document.querySelector(CONFIG.gallerySelector);
    if (!gallery) {
      console.error('[MCK Gallery] No gallery found');
      return;
    }
    
    const normalizedColor = normalizeColor(colorName);
    console.log(`[MCK Gallery] Testing filter with color: "${colorName}" -> normalized: "${normalizedColor}"`);
    filterGalleryImages(gallery, normalizedColor, false);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllGalleries);
  } else {
    initAllGalleries();
  }

  // Re-initialize on Shopify section load (for theme editor)
  document.addEventListener('shopify:section:load', () => {
    setTimeout(initAllGalleries, 100);
  });

  // Expose for debugging - access via browser console
  window.MCKGalleryOptimizer = {
    init: initAllGalleries,
    filter: filterGalleryImages,
    getSelectedColor,
    extractColorFromAlt,
    normalizeColor,
    enableDebug,
    disableDebug,
    testFilter,
    get config() { return CONFIG; }
  };

  // Log initialization
  if (CONFIG.debug) {
    log('Script loaded. Use MCKGalleryOptimizer.enableDebug() for detailed logging.');
  }

})();
